import {
  asset_category_options,
  asset_risk_options,
  asset_rows,
  asset_status_options,
  disposal_rows,
  disposal_stage_options,
  disposal_status_options,
  disposals_cards,
  maintenance_cards,
  maintenance_rows,
  register_cards,
  register_filters,
  tabs
} from './asset_data';
import { asset_drawers } from './asset_drawers';
import { build_disposal_form_state } from './asset_register_helpers';
import {
  asset_currency,
  asset_icon_for_type,
  asset_item,
  asset_risk_class_map,
  asset_risk_score_map,
  asset_status_class_map,
  asset_tone_class,
  create_asset_form,
  create_disposal_form,
  disposal_status_class_map
} from './asset_helpers';

function get_lookup_value(lookup, key, fallback_key) {
  const resolved_value = Reflect.get(lookup, key) || Reflect.get(lookup, fallback_key);
  return resolved_value;
}

function form_text(value, fallback_value) {
  const resolved_value = String(value || '').trim() || fallback_value;
  return resolved_value;
}

function get_asset_risk(label) {
  const asset_risk = { label, class: get_lookup_value(asset_risk_class_map, label, 'Low') };
  return asset_risk;
}

function get_asset_status(label) {
  const asset_status = { label, class: get_lookup_value(asset_status_class_map, label, 'In service') };
  return asset_status;
}

function get_disposal_status(label) {
  const disposal_status = { label, class: get_lookup_value(disposal_status_class_map, label, 'Identified') };
  return disposal_status;
}

function asset_risk_tone(risk_label) {
  const tone_map = { Low: 'green', Medium: 'warning', High: 'danger', Critical: 'danger' };
  const risk_tone = Reflect.get(tone_map, risk_label) || 'danger';
  return risk_tone;
}

function build_asset_metadata(ref, form, row) {
  const asset_metadata = [
    { label: 'Asset ID', value: ref }, { label: 'Category', value: form.category },
    { label: 'Owner / custodian', value: form_text(form.owner, 'Name - role') }, { label: 'Location', value: row.location },
    { label: 'Serial / tag', value: form_text(form.serial, 'Serial pending') }, { label: 'Acquired', value: form_text(form.acquired, 'TBC') },
    { label: 'Supplier', value: form_text(form.supplier, '-') }, { label: 'Contract', value: form_text(form.linked_contract, '-') }
  ];
  return asset_metadata;
}

function build_asset_compliance(form) {
  const warranty = form_text(form.warranty_expiry, 'TBC');
  const insurance = form_text(form.insurance, 'TBC');
  const asset_compliance = [
    asset_item('Warranty / support in date', warranty, 'Pending', 'bg-warning-subtle text-warning-emphasis', 'bi-shield-check', 'orange'), asset_item('Insurance cover', insurance, 'Pending', 'bg-warning-subtle text-warning-emphasis', 'bi-umbrella', 'orange')
  ];
  return asset_compliance;
}

function build_asset_evidence_pack() {
  const asset_evidence_pack = [
    asset_item('Purchase invoice & asset tag', 'Pending', '', '', 'bi-receipt', 'orange'), asset_item('Warranty / support agreement', 'Pending', '', '', 'bi-file-earmark-text', 'orange'),
    asset_item('Insurance schedule', 'Pending', '', '', 'bi-file-earmark-medical', 'orange'), asset_item('Maintenance log', 'Pending', '', '', 'bi-journal-check', 'orange'),
    asset_item('Latest inspection certificate', 'Pending', '', '', 'bi-award', 'orange')
  ];
  return asset_evidence_pack;
}

function build_asset_drawer_payload(ref, form, row, status_label, risk_label) {
  const warranty = form_text(form.warranty_expiry, 'TBC');
  const insurance = form_text(form.insurance, 'TBC');
  const asset_drawer_payload = {
    section: `ASSET - ${ref} - ${form.category.toUpperCase()}`,
    title: row.asset,
    subtitle: `${row.value} - ${row.location}`,
    status: status_label,
    status_class: get_lookup_value(asset_status_class_map, status_label, 'In service'),
    purchase_value: row.value,
    risk_rating: risk_label,
    risk_score: get_lookup_value(asset_risk_score_map, risk_label, 'Low') || '32%',
    risk_tone: asset_risk_tone(risk_label),
    warranty,
    insurance,
    metadata: build_asset_metadata(ref, form, row),
    compliance: build_asset_compliance(form),
    compliance_progress: 'New asset',
    maintenance: [],
    inspections: [],
    incidents: [],
    connected_records: [],
    evidence_progress: '0 of 5',
    evidence_pack: build_asset_evidence_pack(),
    recommended_action: form_text(form.note, 'Complete the linked records, maintenance schedule and evidence pack to bring this asset into the governed compliance workflow.')
  };
  return asset_drawer_payload;
}

function update_tab_count(tabs, tab_id, count) {
  const updated_tabs = tabs.map((tab) => (tab.id === tab_id ? { ...tab, count } : tab));
  return updated_tabs;
}

let asset_sequence = asset_rows.reduce((max, row) => {
  const numeric = Number(String(row.ref || '').replace(/\D/g, ''));
  return Number.isFinite(numeric) ? Math.max(max, numeric) : max;
}, 0);

function next_asset_ref() {
  asset_sequence += 1;
  const asset_ref = `AS-${asset_sequence}`;
  return asset_ref;
}

function build_asset_name(form) {
  const asset_name = (form.asset || '').trim() || 'New asset';
  return asset_name;
}

function build_asset_location(form) {
  const asset_location = (form.location || '').trim() || 'Location pending';
  return asset_location;
}

function build_asset_row(form, ref, risk_label, status_label) {
  const asset_row = {
    ref,
    asset: build_asset_name(form),
    category: form.category,
    location: build_asset_location(form),
    value: asset_currency(form.purchase_value),
    risk: get_asset_risk(risk_label),
    status: get_asset_status(status_label)
  };
  return asset_row;
}

export default {
  name: 'ComplyonAssets',
  data: () => ({
    tabs, active_tab: 'register', register_cards, register_filters, asset_rows, maintenance_cards,
    maintenance_rows, disposals_cards, disposal_rows, asset_drawers, asset_category_options,
    asset_risk_options, asset_status_options, disposal_stage_options, disposal_status_options,
    asset_modal_open: false, asset_form: create_asset_form(), active_asset_drawer_key: null,
    disposal_modal_open: false, disposal_edit_index: null, disposal_form: create_disposal_form()
  }),
  computed: {
    active_asset_drawer() {
      if (!this.active_asset_drawer_key) return null;
      const active_asset_drawer = Reflect.get(this.asset_drawers, this.active_asset_drawer_key) || null;
      return active_asset_drawer;
    }
  },
  methods: {
    set_tab(tab_id) {
      this.active_tab = tab_id;
      this.active_asset_drawer_key = null;
    },
    sync_overlay_state() {
      if (typeof document === 'undefined') return;
      document.body.classList.toggle('assets-overlay-open', this.asset_modal_open || this.disposal_modal_open || Boolean(this.active_asset_drawer_key));
    },
    open_asset_modal() { this.close_asset_drawer(); this.asset_form = create_asset_form(); this.asset_modal_open = true; this.sync_overlay_state(); },
    close_asset_modal() { this.asset_modal_open = false; this.sync_overlay_state(); },
    open_disposal_modal() { this.close_asset_drawer(); this.disposal_edit_index = null; this.disposal_form = create_disposal_form(); this.disposal_modal_open = true; this.sync_overlay_state(); },
    open_disposal_edit_modal(row, index) {
      this.close_asset_drawer();
      this.disposal_edit_index = index;
      this.disposal_form = build_disposal_form_state(row);
      this.disposal_modal_open = true;
      this.sync_overlay_state();
    },
    close_disposal_modal() { this.disposal_modal_open = false; this.disposal_edit_index = null; this.disposal_form = create_disposal_form(); this.sync_overlay_state(); },
    open_asset_drawer(asset_or_ref) {
      const asset_ref = typeof asset_or_ref === 'string' ? asset_or_ref : asset_or_ref?.ref;
      if (!asset_ref) return;
      this.asset_modal_open = false;
      this.active_asset_drawer_key = asset_ref;
      this.sync_overlay_state();
      this.$nextTick(() => {
        const drawer_body = this.$refs.assetDrawerBody;
        if (drawer_body) drawer_body.scrollTop = 0;
      });
    },
    close_asset_drawer() { this.active_asset_drawer_key = null; this.sync_overlay_state(); },
    asset_ref_for_name(asset_name) { return this.asset_rows.find((row) => row.asset === asset_name)?.ref || null; },
    asset_tone_class,
    asset_icon_for_type,
    is_stage_active(index, active_stage) { return index < active_stage; },
    submit_asset_modal() {
      const ref = next_asset_ref();
      const risk_label = this.asset_form.risk_rating;
      const status_label = this.asset_form.compliance_status;
      const new_row = build_asset_row(this.asset_form, ref, risk_label, status_label);
      const drawer_payload = build_asset_drawer_payload(ref, this.asset_form, new_row, status_label, risk_label);
      this.asset_rows.unshift(new_row); this.tabs = update_tab_count(this.tabs, 'register', this.asset_rows.length); Reflect.set(this.asset_drawers, ref, drawer_payload);
      this.close_asset_modal(); this.open_asset_drawer(ref);
    },

    build_disposal_subtitle(form) {
      const reason = form_text(form.reason, 'Disposal reason');
      const method = form.method ? ` - ${form.method}` : '';
      const value = form.salvage_value ? ` - R ${form.salvage_value}` : '';
      return `${reason}${method}${value}`;
    },

    build_disposal_route(form) {
      const route = form_text(form.approval_route, 'Owner - CFO');
      const note = form_text(form.note, 'Governed disposal in progress.');
      return `Approval route: ${route} - ${note}`;
    },

    build_disposal_row(form) {
      const active_stage = Math.max(1, this.disposal_stage_options.indexOf(form.stage) + 1);
      const row = {
        ref: form.ref || next_asset_ref(),
        asset: form_text(form.asset, 'New disposal'),
        subtitle: this.build_disposal_subtitle(form),
        status: get_disposal_status(form.status),
        stages: [...this.disposal_stage_options],
        active_stage,
        route: this.build_disposal_route(form)
      };
      return row;
    },

    submit_disposal_modal() {
      const row = this.build_disposal_row(this.disposal_form);
      if (this.disposal_edit_index === null) {
        this.disposal_rows.unshift(row);
        this.tabs = update_tab_count(this.tabs, 'disposals', this.disposal_rows.length);
      } else {
        this.$set(this.disposal_rows, this.disposal_edit_index, row);
      }
      this.close_disposal_modal();
    },
    delete_disposal_modal() {
      if (this.disposal_edit_index === null) return;
      this.disposal_rows.splice(this.disposal_edit_index, 1);
      this.tabs = update_tab_count(this.tabs, 'disposals', this.disposal_rows.length);
      this.close_disposal_modal();
    }
  },
  mounted() { this.sync_overlay_state(); },
  beforeDestroy() { if (typeof document !== 'undefined') document.body.classList.remove('assets-overlay-open'); },
  watch: {
    asset_modal_open() { this.sync_overlay_state(); },
    disposal_modal_open() { this.sync_overlay_state(); },
    active_asset_drawer_key() { this.sync_overlay_state(); }
  }
};
